import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { RelevanceBadge, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Download, ExternalLink, Calendar, Mail, User, Building2, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
    <div className="text-sm text-zinc-900">{children || <span className="text-zinc-400">—</span>}</div>
  </div>
);

const FmtDate = ({ value }) => {
  if (!value) return <span className="text-zinc-400">—</span>;
  return <span className="font-mono">{new Date(value).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}</span>;
};

export default function OpportunityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [opp, setOpp] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = () => api.opportunity(id).then(setOpp).catch(() => setOpp({ error: true }));
  useEffect(() => { load(); }, [id]);

  const dl = async (url) => {
    setDownloading(url);
    try {
      const r = await api.downloadDoc(id, url);
      if (r.status === "downloaded") toast.success(`Downloaded ${r.document?.attachment_filename}`);
      else if (r.status === "skipped_login") toast.info("Login required at source portal — URL preserved.");
      else toast.error(`Download failed: ${r.document?.error || r.status}`);
      load();
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  if (!opp) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (opp.error) return <div className="p-8 text-zinc-500">Not found.</div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="opportunity-detail-page">
      <button
        className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-[#002FA7] mb-4 inline-flex items-center gap-1"
        onClick={() => nav(-1)}
        data-testid="back-btn"
      >
        <ArrowLeft size={12} /> Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{opp.source_platform}</span>
            <span className="text-zinc-300">·</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{opp.rfp_id}</span>
            <StatusBadge status={opp.status} active={opp.active_flag} days={opp.days_remaining} />
          </div>
          <h1 className="text-3xl font-bold font-[Chivo] tracking-tight leading-tight" data-testid="opp-title">
            {opp.rfp_title}
          </h1>
          <div className="mt-2 text-sm text-zinc-600 flex items-center gap-2">
            <Building2 size={14} className="text-zinc-400" />
            <span className="font-medium">{opp.buyer_name}</span>
            <span className="text-zinc-300">·</span>
            <span>{opp.buyer_type}</span>
            <span className="text-zinc-300">·</span>
            <span>{opp.province}</span>
          </div>
        </div>
        <div className="text-right">
          <RelevanceBadge score={opp.relevance_score} />
          <div className="mt-2">
            <Button asChild size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#002175] text-white">
              <a href={opp.source_url} target="_blank" rel="noreferrer" data-testid="source-link-btn">
                <ExternalLink size={12} className="mr-1" /> Open on source
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Relevance reasoning */}
      <div className="border border-zinc-200 bg-[#FAFAFA] p-5 mt-6" data-testid="relevance-card">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Relevance Analysis</div>
        <div className="text-sm text-zinc-700">{opp.relevance_reason}</div>
        <div className="mt-3 text-xs text-zinc-500 font-mono">Category: <span className="text-zinc-900">{opp.category_primary}</span></div>
        {opp.keywords_matched?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {opp.keywords_matched.map(k => (
              <Badge key={k} variant="outline" className="rounded-sm text-[10px] font-mono border-zinc-300 text-zinc-700">
                {k}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Dates + Contact + Org */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-zinc-200 bg-white p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-2">
            <Calendar size={12} /> Timeline
          </div>
          <Field label="Issued" testId="field-issue-date"><FmtDate value={opp.issue_date} /></Field>
          <Field label="Questions Due" testId="field-question-deadline"><FmtDate value={opp.question_deadline} /></Field>
          <Field label="Closing" testId="field-closing-deadline"><FmtDate value={opp.closing_deadline} /></Field>
          <Field label="Days Remaining" testId="field-days-remaining">
            {typeof opp.days_remaining === "number" ? (
              <span className={`font-mono font-bold ${opp.days_remaining < 0 ? "text-zinc-400" : opp.days_remaining <= 7 ? "text-[#E00000]" : "text-zinc-900"}`}>
                {opp.days_remaining}d
              </span>
            ) : "—"}
          </Field>
        </div>

        <div className="border border-zinc-200 bg-white p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-2">
            <User size={12} /> Contact
          </div>
          <Field label="Name" testId="field-contact-name">{opp.contact_name}</Field>
          <Field label="Email" testId="field-contact-email">
            {opp.contact_email ? (
              <a href={`mailto:${opp.contact_email}`} className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                <Mail size={12} /> {opp.contact_email}
              </a>
            ) : null}
          </Field>
          <Field label="Phone" testId="field-contact-phone">{opp.contact_phone}</Field>
          <Field label="Language" testId="field-language">{(opp.language || "en").toUpperCase()}</Field>
        </div>

        <div className="border border-zinc-200 bg-white p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-2">
            <Building2 size={12} /> Organisation
          </div>
          <Field label="Sector" testId="field-sector">{opp.sector}</Field>
          <Field label="Employees" testId="field-employee-size">
            {opp.employee_size ? (
              <span>
                <span className="font-mono font-bold">{opp.employee_size.toLocaleString()}</span>
                <span className="text-[10px] text-zinc-500 ml-2 font-mono">
                  ({opp.organization_size_source || "unknown source"})
                </span>
              </span>
            ) : null}
          </Field>
          {opp.student_count && (
            <Field label="Students" testId="field-students">
              <span className="font-mono">{opp.student_count.toLocaleString()}</span>
            </Field>
          )}
          {opp.housing_units && (
            <Field label="Housing units" testId="field-housing-units">
              <span className="font-mono">{opp.housing_units.toLocaleString()}</span>
            </Field>
          )}
          {opp.organization_website && (
            <Field label="Website" testId="field-website">
              <a href={opp.organization_website} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">
                {opp.organization_website}
              </a>
            </Field>
          )}
        </div>
      </div>

      {/* Scope */}
      <div className="border border-zinc-200 bg-white p-5 mt-6" data-testid="scope-card">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Scope / Summary</div>
        <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-line">
          {opp.scope_excerpt || opp.summary || "No scope provided."}
        </p>
      </div>

      {/* Documents */}
      <div className="border border-zinc-200 bg-white mt-6" data-testid="documents-card">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-2">
            <FileText size={12} /> Documents ({opp.document_count || 0})
          </div>
          {opp.login_required && (
            <div className="text-[10px] uppercase tracking-widest text-[#E00000] font-mono flex items-center gap-1">
              <AlertCircle size={12} /> Login required at source
            </div>
          )}
        </div>
        <div>
          {(opp.documents || []).length === 0 && (
            <div className="px-5 py-6 text-sm text-zinc-500">No documents catalogued.</div>
          )}
          {(opp.documents || []).map((d, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 last:border-0"
              data-testid={`doc-row-${i}`}
            >
              <div className="min-w-0">
                <div className="text-sm text-zinc-900 truncate">{d.attachment_filename || d.attachment_url}</div>
                <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                  {d.attachment_type || "unknown type"}
                  {d.size_bytes && ` · ${(d.size_bytes / 1024).toFixed(1)} KB`}
                  {d.attachment_hash && ` · sha256:${d.attachment_hash.slice(0, 12)}…`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 border"
                  style={{
                    borderColor:
                      d.attachment_download_status === "downloaded" ? "#00B140" :
                      d.attachment_download_status === "failed" ? "#E00000" :
                      d.attachment_download_status === "skipped_login" ? "#FFB000" : "#A0A0A0",
                    color:
                      d.attachment_download_status === "downloaded" ? "#00B140" :
                      d.attachment_download_status === "failed" ? "#E00000" :
                      d.attachment_download_status === "skipped_login" ? "#FFB000" : "#52525B",
                  }}
                >
                  {d.attachment_download_status}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm border-zinc-300 text-zinc-700"
                  onClick={() => dl(d.attachment_url)}
                  disabled={downloading === d.attachment_url || d.attachment_download_status === "downloaded"}
                  data-testid={`download-doc-btn-${i}`}
                >
                  <Download size={12} className="mr-1" />
                  {downloading === d.attachment_url ? "…" : "Download"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
